import { NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Bell,
  BookmarkPlus,
  BookOpenCheck,
  CalendarClock,
  History,
  MessagesSquare,
  Server,
  Settings,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { ChatPage } from "./pages/ChatPage";
import { HistoryPage } from "./pages/HistoryPage";
import { AuditPage } from "./pages/AuditPage";
import { SettingsPage } from "./pages/SettingsPage";
import { LoginPage } from "./pages/LoginPage";
import { SavedQuestionsPage } from "./pages/SavedQuestionsPage";
import { RulesPage } from "./pages/RulesPage";
import { DigestsPage } from "./pages/DigestsPage";
import { RecyclePage } from "./pages/RecyclePage";
import { LicenseBadge } from "./components/LicenseBadge";
import { TourLauncher, TourOverlay, tourForPath, useAutoStartTour } from "./components/tour";
import { api, type ConfigResponse } from "./lib/api";

export function App() {
  return (
    <div className="grid min-h-screen grid-rows-[auto,1fr] bg-datumcraft-fog">
      <Header />
      <main className="min-h-0">
        <Routes>
          <Route path="/" element={<Navigate to="/chat" replace />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/history" element={<HistoryPage />} />
          <Route path="/audit" element={<AuditPage />} />
          <Route path="/saved" element={<SavedQuestionsPage />} />
          <Route path="/rules" element={<RulesPage />} />
          <Route path="/digests" element={<DigestsPage />} />
          <Route path="/recycle" element={<RecyclePage />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Routes>
      </main>
      <RouteTourBinder />
      <TourOverlay />
    </div>
  );
}

/**
 * Auto-registers + auto-starts the tour for the current route. Co-located
 * with App so the routing→tour mapping lives in one place; pages don't
 * each have to remember to call useAutoStartTour. localStorage gates
 * dismissal — auto-start fires once per browser per tour.id.
 *
 * Routes without a registered tour (/, /login) render nothing; the inner
 * <BoundTour> only mounts when a tour exists, keeping the hook order
 * stable and avoiding "no-op tour" UX glitches.
 */
function RouteTourBinder() {
  const { pathname } = useLocation();
  const tour = tourForPath(pathname);
  if (!tour) return null;
  return <BoundTour key={tour.id} />;
}

function BoundTour() {
  const { pathname } = useLocation();
  const tour = tourForPath(pathname)!;
  useAutoStartTour(tour);
  return null;
}

function Header() {
  const { data: cfg } = useQuery<ConfigResponse>({
    queryKey: ["app-config"],
    queryFn: async () => (await api.get<ConfigResponse>("/config")).data,
    staleTime: 60_000,
    retry: false,
  });

  return (
    <header className="sticky top-0 z-20 border-b border-datumcraft-slate/25 bg-white/85 backdrop-blur">
      <div className="flex w-full items-center gap-6 px-6 py-2.5">
        <a href="/" className="flex items-center gap-2.5 font-semibold text-datumcraft-navy">
          <Logo />
          <div className="leading-tight">
            <div className="font-display text-[15px]">DatumCraft Operate</div>
            <div className="text-2xs font-normal text-datumcraft-slate">
              Cognos intelligence, asked in plain English
            </div>
          </div>
        </a>
        <nav aria-label="primary" data-tour-id="nav" className="flex gap-1 text-sm">
          <NavItem to="/chat"    icon={<MessagesSquare size={14} />}>Chat</NavItem>
          <NavItem to="/saved"   icon={<BookmarkPlus size={14} />}>Saved</NavItem>
          <NavItem to="/digests" icon={<CalendarClock size={14} />}>Digests</NavItem>
          <NavItem to="/rules"   icon={<Bell size={14} />}>Rules</NavItem>
          <NavItem to="/history" icon={<History size={14} />}>History</NavItem>
          <NavItem to="/recycle" icon={<Trash2 size={14} />}>Recycle</NavItem>
          <NavItem to="/audit"   icon={<BookOpenCheck size={14} />}>Audit</NavItem>
          <NavItem to="/settings" icon={<Settings size={14} />}>Settings</NavItem>
        </nav>
        <div className="ml-auto flex items-center gap-2 text-2xs text-datumcraft-slate">
          {cfg?.instanceName && <InstanceNameBadge name={cfg.instanceName} />}
          {cfg?.llmProvider && <ProviderBadge name={cfg.llmProvider} />}
          {cfg?.semanticLayerVersion && (
            <span
              title={`Semantic layer ${cfg.semanticLayerVersion}`}
              data-tour-id="grounded-badge"
              className="inline-flex items-center gap-1 rounded-full bg-datumcraft-success/10 px-2 py-0.5 font-medium text-datumcraft-success"
            >
              <ShieldCheck size={11} /> Grounded
            </span>
          )}
          <LicenseBadge />
          <TourLauncher />
        </div>
      </div>
    </header>
  );
}

function NavItem({
  to,
  icon,
  children,
}: {
  to: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 font-medium transition-colors ${
          isActive
            ? "bg-datumcraft-navy text-white shadow-sm"
            : "text-datumcraft-ink hover:bg-datumcraft-mist"
        }`
      }
    >
      <span aria-hidden>{icon}</span>
      <span>{children}</span>
    </NavLink>
  );
}

/**
 * Operator-set deployment label — appears as a chip in the header so
 * an operator running multiple bundles (dev / staging / customer A /
 * customer B) can tell them apart at a glance. Set on the Settings
 * page; persisted in the internal DB; read from
 * {@code GET /api/v1/config}.
 */
function InstanceNameBadge({ name }: { name: string }) {
  return (
    <span
      title={`Instance — ${name}`}
      data-tour-id="instance-name-badge"
      className="inline-flex items-center gap-1 rounded-full bg-datumcraft-navy px-2 py-0.5 font-medium text-white"
    >
      <Server size={11} /> {name}
    </span>
  );
}

function ProviderBadge({ name }: { name: string }) {
  const label =
    name === "anthropic" ? "Claude" :
    name === "azure-openai" ? "Azure OpenAI" :
    name === "watsonx" ? "watsonx" :
    name === "self-hosted" ? "Self-hosted" : name;
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-datumcraft-slate/30 bg-white px-2 py-0.5 font-medium text-datumcraft-steel">
      <span className="h-1.5 w-1.5 rounded-full bg-datumcraft-accent" aria-hidden />
      {label}
    </span>
  );
}

// Product glyph — chat bubble + focal cyan dot (the AI answer at the
// center of the conversation). Mirrors `GlyphOperate` from the website's
// brand system at `website/app/brand/products/glyphs.tsx`. Outline uses
// currentColor so it follows the parent text-* class; the focal dot is
// the brand cyan #1FBFBC.
function Logo() {
  return (
    <span
      className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-datumcraft-navy text-white shadow-sm"
      aria-hidden
    >
      <svg
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path
          d="M4 7.5 a3 3 0 0 1 3-3 h10 a3 3 0 0 1 3 3 v6 a3 3 0 0 1 -3 3 h-4 l-3.5 3 v-3 h-2.5 a3 3 0 0 1 -3 -3z"
          stroke="currentColor"
          strokeWidth={1.6}
        />
        <circle cx="12" cy="10.5" r="1.6" fill="#1FBFBC" />
      </svg>
    </span>
  );
}
